import { AccountTypes } from '@interfaces/models/account-types';
import { IReceipt } from '@interfaces/models/receipt.interface';
import { SuffixValuesEnum } from '@interfaces/open-account/enums';
import {
  AANAccountType,
  IRADesignationListedTypes,
  IRAOptions,
  IRATypes,
  ISelectedAccount,
  ISelectedClientAccountsState,
  Move_To_New,
  TransactionTypesEnum,
} from '@shared/models';
import { ReceiptModal } from '@shared/models/receipt-modal';
import { AccountTriageContent } from '@shared/services/content/content-jsons/account-triage-content';
import { BeneRelationshipContent } from '@shared/services/content/content-jsons/bene-relationship-content';
import { TransactionState } from '@store/reducers';
import { BeneficiariesState, Person } from '@store/reducers/beneficiaries/beneficiaries.reducer';
import { MfePersonalInfoClientState } from '@store/reducers/mfe-personal-info-client/mfe-personal-info-client.reducer';
import { IOpenAccountState } from '@store/reducers/open-account/open-account.reducer';
import {
  getHasEligibleExistingAccount,
  getIraOption,
} from '@store/reducers/selected-client-accounts/selected-client-accounts.reducer';
import {
  getIsClient,
  getIsIRAAccount,
  getIsIRAExecutor,
  getIsIRAListedByRelationship,
  getIsIRASingleExecutor,
  getIsIRATrust,
  getIsSingleExecutor,
  getIsSpouse,
  TriageState,
} from '@store/reducers/triage/triage.reducer';

import { IRATransferToType, joinWithSpace } from '../cashier-nonretirement';
import { buildSettlementFundRow } from '../receipt.helpers';

/**
 * Returns a boolean to determines if a user will be shown the transfer-to page
 */
export function canChooseExisting(
  guideState: TriageState,
  selectedAccounts: ISelectedClientAccountsState,
) {
  return (
    getIsClient(guideState) &&
    getIsSpouse(guideState) &&
    getHasEligibleExistingAccount(selectedAccounts) &&
    getIraOption(selectedAccounts) === IRAOptions.ASSUME
  );
}

export function buildBeneByRelationshipReceipt(
  currentTransaction: TransactionState | null,
  state: BeneficiariesState,
  triage: {
    accountTypeInheriting: AccountTypes;
    IRADesignation: IRADesignationListedTypes;
    isSpouse: string;
  },
  personalInfo: MfePersonalInfoClientState['personalInfo'],
  editable: boolean,
  isMulti = false,
) {
  const isNotIRAAccount = !getIsIRAAccount(triage as TriageState);
  const isNotIRAListedByRelationship = !getIsIRAListedByRelationship(triage as TriageState);
  const isSpouse = getIsSpouse(triage as TriageState);
  if (isNotIRAAccount || isNotIRAListedByRelationship || isSpouse) return null;

  const relationshipDesignation = BeneRelationshipContent.beneRelationshipOptions.find(
    (option) => option.key === state.relationshipDesignation,
  );
  const receipt: IReceipt = {
    header: 'Beneficiary designation information',
    editable: editable,
    editRoute: !isMulti
      ? 'secure/inheritance/beneficiaries/details'
      : 'secure/multi-inheritance/account-triage',
    editTransferId: currentTransaction?.currentTransactionId,
    sectionEvidenceID: 'beneByRelationship',
    rows: [],
  };

  if (!isMulti) {
    receipt.rows.push({
      label: 'Designation',
      values: [relationshipDesignation?.value ?? ''],
      evidenceID: 'designation',
    });
  }

  receipt.rows.push({
    label: 'Beneficiary (self)',
    values: [
      joinWithSpace([
        personalInfo.firstName,
        personalInfo.middleName,
        personalInfo.lastName,
        SuffixValuesEnum[personalInfo.suffix],
      ]).trim(),
    ],
    evidenceID: 'beneficiarySelf',
  });

  state.otherBenes.forEach((bene: Person, i: number) => {
    const isDeceased = isDateOfDeath(bene.dateGroup.dod) ? ' (deceased)' : '';
    receipt.rows.push({
      label: `Beneficiary ${i + 1}${isDeceased}`,
      values: [
        bene.name,
        isDateOfDeath(bene.dateGroup.dod)
          ? `${bene.dateGroup.dob} - ${bene.dateGroup.dod}`
          : `${bene.dateGroup.dob}`,
      ],
      evidenceID: `beneficiary${i + 1}`,
      hideBorder: bene.children?.length! > 0 || (isMulti && isDateOfDeath(bene.dateGroup.dod)),
    });

    if (isMulti && isDateOfDeath(bene.dateGroup.dod)) {
      receipt.rows.push({
        label: AccountTriageContent.beneficiaryCard.hasChildrendQuestion,
        values: [bene.children?.length ? 'Yes' : 'No'],
        evidenceID: `hasChildrendQuestion${i + 1}`,
        hideBorder: bene.children?.length! > 0,
        isLabelBold: true,
      });
    }

    if (bene.children?.length! > 0) {
      bene.children!.forEach((child: Person, j: number) => {
        const isDeceased = isDateOfDeath(child.dateGroup.dod) ? ' (deceased)' : '';
        receipt.rows.push({
          label: `Child ${j + 1}${isDeceased}`,
          values: [
            child.name,
            isDateOfDeath(child.dateGroup.dod)
              ? `${child.dateGroup.dob} - ${child.dateGroup.dod}`
              : `${child.dateGroup.dob}`,
          ],
          evidenceID: `child${j + 1}`,
          hideBorder: j < bene.children!.length - 1,
        });
      });
    }
  });

  return receipt;
}

export function buildAccountFromIRA(
  guideState: TriageState,
  selectedAccounts: ISelectedClientAccountsState,
  prospect: boolean,
  editable: boolean,
) {
  const edit = editable ? 'YES' === guideState.isSpouse : false;
  const iraOption = selectedAccounts.transferTo.iraOption;
  const iraType = selectedAccounts.transferTo.iraType;
  const isIRASingleExecutor = getIsIRASingleExecutor(guideState);

  const receipt: IReceipt = {
    header: 'Transferring from',
    editable: edit,
    editRoute: 'secure/inheritance/ira-type',
    sectionEvidenceID: 'accountFrom',
    rows: [],
  };
  let accountTypeValues = ['IRA'];

  if (iraType === IRATypes.ROTH) {
    accountTypeValues = ['Roth IRA'];
  } else if (iraType === IRATypes.INHERITED_ROTH) {
    accountTypeValues = ['Inherited Roth IRA'];
  } else if (iraType === IRATypes.INHERITED_OTHER) {
    accountTypeValues = ['Inherited Traditional IRA'];
  } else if (iraType === IRATypes.OTHER) {
    accountTypeValues = ['Traditional, rollover or SEP-IRA'];
  }

  if (!isIRASingleExecutor) {
    receipt.rows.push({
      label: 'Transfer Option',
      values: [IRAOptions.ASSUME === iraOption ? 'Assume' : 'Inherit'],
      evidenceID: 'transferOption',
    });
  }

  receipt.rows.push(
    {
      label: 'Account type',
      values: accountTypeValues,
      evidenceID: 'accountType',
    },
    {
      label: 'Source of Funds',
      values: ['Family/Inheritance'],
      evidenceID: 'sourceOfFunds',
    },
  );
  return receipt;
}

function getAccountTypeToValue(transferTo: ISelectedClientAccountsState['transferTo']) {
  const OtherIRA = () =>
    IRAOptions.ASSUME === transferTo.iraOption ? 'Traditional IRA' : 'Inherited Traditional IRA';
  const RothIRA = () =>
    IRAOptions.ASSUME === transferTo.iraOption ? 'Roth IRA' : 'Inherited Roth IRA';

  if (IRATypes.ROTH === transferTo.iraType || IRATypes.INHERITED_ROTH === transferTo.iraType) {
    return RothIRA();
  }
  return OtherIRA();
}

function getExistingAccountTypeToValue(accountType: AANAccountType) {
  let assumeValue;
  switch (accountType) {
    case AANAccountType.TRADITIONAL_IRA:
      assumeValue = 'Traditional IRA';
      break;
    case AANAccountType.ROLLOVER_IRA:
      assumeValue = 'Rollover IRA';
      break;
    case AANAccountType.SEP_IRA_SINGLE_PARTICIPANT:
      assumeValue = 'SEP IRA';
      break;
    case AANAccountType.ROTH_IRA:
      assumeValue = 'Roth IRA';
      break;
    default:
      assumeValue = 'IRA';
      break;
  }
  return assumeValue;
}

export function buildAccountToIRA(
  triageState: TriageState,
  selectedAccounts: ISelectedClientAccountsState,
  openAccount: IOpenAccountState,
  editable: boolean,
  VCDToggleOn: boolean = false,
) {
  const toAccount: ISelectedAccount = selectedAccounts.transferTo.selectedAccount;
  const edit = editable && canChooseExisting(triageState, selectedAccounts);
  const receipt: IReceipt = {
    header: 'Transferring to',
    editable: edit || false,
    editRoute: edit ? 'secure/inheritance/transfer-to' : '',
    sectionEvidenceID: 'accountTo',
    rows: [],
  };

  const isIRAExecutor = getIsIRAExecutor(triageState);
  const isSingleExecutor = getIsSingleExecutor(triageState);
  const isIRAToEstate = isIRAExecutor && isSingleExecutor;
  const isIRAToTrust = getIsIRATrust(triageState);
  const shouldShowMFESettlementFundSection = VCDToggleOn && isIRAToTrust;

  if (isIRAToEstate) {
    receipt.rows.push({
      label: 'Account type',
      values: [
        IRATransferToType[selectedAccounts.transferTo.iraType] || 'Estate brokerage account',
      ],
      evidenceID: 'toAccountType',
    });

    return receipt;
  }

  if (selectedAccounts.transactionType === TransactionTypesEnum.EXISTING && toAccount) {
    // ONLY: client spouse assume existing
    receipt.rows.push(
      {
        label: 'Account type',
        values: [getExistingAccountTypeToValue(toAccount.accountData.accountType)],
        evidenceID: 'toAccountType0',
      },
      {
        label: 'Account number',
        values: [toAccount.accountNumber],
        evidenceID: 'toAccountNumber0',
      },
    );
  } else {
    receipt.rows.push(
      {
        label: 'Account type',
        values: [getAccountTypeToValue(selectedAccounts.transferTo)],
        evidenceID: 'toAccountType',
      },
      {
        label: 'Account number',
        values: [
          toAccount?.accountNumber === 'newAccount' ||
          !toAccount?.accountNumber ||
          openAccount.openAccountResponse?.isS24Reject
            ? 'Account number pending'
            : toAccount.accountNumber,
        ],
        evidenceID: 'toAccountNumber',
      },
      {
        label: 'Dividends & capital gains',
        values: ['Reinvest distributions'],
        modal: 'divcapgains-modal',
        evidenceID: 'divAndCap',
      },
      buildSettlementFundRow(shouldShowMFESettlementFundSection, openAccount?.settlementFundChoice),
    );
  }

  return receipt;
}

export function buildDefaultBeneficiaryReceipt(
  triageState: TriageState,
  transactionType: TransactionTypesEnum,
  accountType: AccountTypes,
): IReceipt | null {
  const isIRASingleExecutor = getIsIRASingleExecutor(triageState);
  const receipt: IReceipt = {
    header: 'Beneficiaries',
    editable: false,
    editRoute: '',
    sectionEvidenceID: 'defaultBeneficiaries',
    rows: [
      {
        label: '',
        values: [
          `Assets will pass to your surviving spouse or your estate. This is the current default according to Vanguard\'s IRA Custodial Account Agreement.`,
        ],
        modal: ReceiptModal.BENEFICIARIES,
        evidenceID: 'defaultBene',
      },
    ],
  };

  return Move_To_New.has(transactionType) &&
    AccountTypes.IRA === accountType &&
    !isIRASingleExecutor
    ? receipt
    : null;
}

function isDateOfDeath(date: string | undefined): boolean {
  return !!date && date !== 'N/A';
}
